import React, { FC } from "react";
import { Button } from "antd";
import { MenuOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store/store";
import { setMobileMenuOpen } from "../../store/slices/layout/layoutSlice";
import Logo from "../logo/Logo";
import MainMenu from "../navigation/MainMenu";
import ApplicationApps from "./ApplicationApps/ApplicationApps";
import ChatDropdown from "./dropdown/chat/Chat";
import User from "./dropdown/user/User";
import SettingsLauncher from "./dropdown/SettingsLauncher/SettingsLauncher";
import LanguageHead from "./dropdown/language/Language";
import BackgroundJobWatcher from "../../components/jobs/BackgroundJobWatcher";
import { FontSizeSwitcher } from "../../theme/fontScale";

interface HeaderMainProps {
  fixed?: boolean;
  showNavigation?: boolean;
}

// DashLite white header (matches CRM shell) — see docs/pms-design-system.md §2.
// Mobile (<lg) keeps the antd MobileNavigation drawer via layoutSlice.mobileMenuOpen.
const HeaderMain: FC<HeaderMainProps> = ({
  fixed = true,
  showNavigation = true,
}) => {
  const dispatch = useDispatch();
  const { mobileMenuOpen } = useSelector((state: RootState) => state.layout);

  const headerClass = `nk-header is-regular is-light${
    fixed ? " nk-header-fixed" : ""
  }`;

  return (
    <div className={headerClass} style={{ boxShadow: "0 1px 3px rgba(2,44,87,.05)" }}>
      {/* Watches backgrounded export/import jobs; toasts + badges on completion.
          Mounted here so it lives once inside every authenticated layout. */}
      <BackgroundJobWatcher />
      <div className="container-fluid">
        <div className="nk-header-wrap">
          <div className="nk-menu-trigger me-sm-2 d-lg-none">
            <Button
              type="text"
              aria-label="Open menu"
              icon={<MenuOutlined style={{ fontSize: 20, color: "#526484" }} />}
              onClick={() => dispatch(setMobileMenuOpen(!mobileMenuOpen))}
            />
          </div>
          <div className="nk-header-brand d-flex align-items-center">
            <Logo />
            {/* Names the product this shell is — sits right of the ProcureX mark,
                behind a hairline rule. Hidden under md so the mark + nav still fit. */}
            <span
              className="d-none d-md-inline-block"
              style={{
                marginLeft: 12,
                paddingLeft: 12,
                borderLeft: "1px solid #e5e9f2",
                fontSize: 13,
                fontWeight: 600,
                letterSpacing: "-0.01em",
                color: "#022c57",
                whiteSpace: "nowrap",
                lineHeight: 1.2,
              }}
            >
              Project Management System
            </span>
          </div>
          {showNavigation && (
            // centred via .is-centered in core/layouts/_header.scss
            <div className="nk-header-menu is-centered d-none d-lg-flex">
              <MainMenu />
            </div>
          )}
          <div className="nk-header-tools py-0">
            <ul className="nk-quick-nav">
              {/* A/A+/A++ app-wide text size — preference persists via localStorage */}
              <li className="d-none d-sm-flex" style={{ alignItems: "center" }}>
                <FontSizeSwitcher />
              </li>
              {/* Customer/org logo — same source and placement as the CRM header */}
              <li className="language-dropdown d-none d-sm-block me-n1">
                <LanguageHead />
              </li>
              <li style={{ display: "flex", alignItems: "center" }}>
                <ApplicationApps />
              </li>
              <li style={{ display: "flex", alignItems: "center" }}>
                <SettingsLauncher />
              </li>
              <li className="chats-dropdown">
                <ChatDropdown />
              </li>
              <li className="user-dropdown">
                <User />
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HeaderMain;
